import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { LogIn, AlertCircle, ScanFace } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [faceIDLoading, setFaceIDLoading] = useState(false);
  const [hasFaceID, setHasFaceID] = useState(false);

  useEffect(() => {
    // Check if Face ID bridge is available (iOS native app)
    const faceID = (window as any).SAIK?.faceID;
    setHasFaceID(!!faceID?.available);

    // Listen for auto-login event from iOS when JWT expired but credentials are saved
    const handleAutoLogin = (event: any) => {
      const { username, password } = event.detail || {};
      if (username && password) {
        setUsername(username);
        setPassword(password);
        setLoading(true);
        login(username, password).then(() => navigate('/app')).catch((err: any) => {
          setError(err?.response?.data?.detail || 'Auto-login failed');
          setLoading(false);
        });
      }
    };
    window.addEventListener('saik:autologin', handleAutoLogin);
    return () => window.removeEventListener('saik:autologin', handleAutoLogin);
  }, []);

  const handleFaceID = async () => {
    setError('');
    setFaceIDLoading(true);
    try {
      const result = await (window as any).SAIK.faceID.request();
      if (result.username && result.password) {
        setUsername(result.username);
        setPassword(result.password);
        // Auto-submit login with retrieved credentials
        setLoading(true);
        await login(result.username, result.password);
        navigate('/app');
      }
    } catch (err: any) {
      setError(err || 'Face ID authentication failed.');
    } finally {
      setFaceIDLoading(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/app');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white dark:bg-slate-900 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-purple-600 shadow-lg shadow-brand-500/30" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="login-logo" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#c7d2fe" />
                  <stop offset="1" stopColor="#e9d5ff" />
                </linearGradient>
              </defs>
              <path d="M16 40 L20 24 L26 32 L32 20 L38 32 L44 24 L48 40 Z" fill="url(#login-logo)" opacity="0.98" />
              <path d="M16 40 L48 40 L46 44 L18 44 Z" fill="white" opacity="0.85" />
              <circle cx="32" cy="36" r="3" fill="url(#login-logo)" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-950 dark:text-slate-100">SAIK Dashboard</h1>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-400">Sign in to your account</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 p-6 shadow-xl"
        >
          {error && (
            <div role="alert" className="mb-4 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-400">
              <AlertCircle size={16} aria-hidden="true" />
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="login-username" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
              Email or username
            </label>
            <input
              id="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 placeholder-slate-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Enter email or username"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 placeholder-slate-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Enter password"
            />
          </div>

          {hasFaceID && (
            <>
              <div className="mb-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                <span className="text-xs text-slate-500 dark:text-slate-400">or</span>
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              </div>
              <button
                type="button"
                onClick={handleFaceID}
                disabled={faceIDLoading || loading}
                className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm font-medium text-slate-950 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
              >
                {faceIDLoading ? <LoadingSpinner size="sm" /> : <ScanFace size={18} />}
                {faceIDLoading ? 'Authenticating...' : 'Sign in with Face ID'}
              </button>
            </>
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? <LoadingSpinner size="sm" /> : <LogIn size={18} />}
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          <p className="mt-4 text-center text-sm text-slate-700 dark:text-slate-400">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-brand-700 dark:text-brand-400 hover:text-brand-300">
              Register
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
